import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import type { TimeEntry, Project, Location, Profile } from '../types';
import { calculateTimeBlocks, consolidateProjects } from './timeCalculator';

interface ExportBlock {
  date: string;
  timeArrived: string;
  timeLeft: string;
  breakHours: number | null;
  description: string;
  location?: string;
  workMinutes?: number;
}
import { getDaysInMonth, parseISO, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ZE_TEMPLATE_BASE64 } from './templateBase64';
import { supabase } from '../lib/supabase';

export async function loadTemplateBuffer(): Promise<ArrayBuffer> {
  // 1. Browser environment: try fetching from public/templates
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    try {
      const basePath = import.meta.env?.BASE_URL || '/';
      const cleanBase = basePath.endsWith('/') ? basePath : basePath + '/';
      const response = await fetch(`${cleanBase}templates/ZE_Template.xlsx`);
      if (response.ok) {
        return await response.arrayBuffer();
      }
    } catch {
      // Fall through to embedded base64
    }
  }

  // 2. Embedded base64 fallback (works in browser, tests, offline, etc.)
  const binaryString = atob(ZE_TEMPLATE_BASE64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export function saveWorkbookFile(buffer: ExcelJS.Buffer, fileName: string): void {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    if (typeof URL.createObjectURL === 'function') {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}

export function timeStringToExcelFraction(t?: string | null): number | null {
  if (!t) return null;
  const parts = t.split(':').map(Number);
  if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
  const hours = parts[0];
  const minutes = parts[1];
  const seconds = parts.length > 2 && !isNaN(parts[2]) ? parts[2] : 0;
  return (hours * 3600 + minutes * 60 + seconds) / 86400;
}

export function incrementMinorVersion(version: string = 'v0.1'): string {
  const match = version.match(/^v?(\d+)\.(\d+)$/);
  if (!match) return 'v0.1';
  const major = match[1];
  const minor = parseInt(match[2], 10) + 1;
  return `v${major}.${minor}`;
}

export function getMonthlyReportFileName(
  monthString: string,
  employeeNumber?: string | null,
  initials?: string | null,
  version: string = 'v0.1'
): string {
  const formattedMonth = monthString.replace('-', '.');
  const cleanEmpNo = (employeeNumber || '').trim();
  const cleanInitials = (initials || '').trim().toUpperCase();
  let verSuffix = '';
  if (version) {
    const normalizedVer = version.startsWith('v') ? version : `v${version}`;
    verSuffix = `_${normalizedVer}`;
  }
  return `SPF_ZE_${cleanEmpNo}_${cleanInitials}_${formattedMonth}${verSuffix}.xlsx`;
}

export interface ExportToExcelOptions {
  uploadCopy?: boolean;
  employeeNumber?: string | null;
  initials?: string | null;
  employeeName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  userProfile?: Profile | null;
  isApproval?: boolean;
}

export function bufferToBase64(buffer: ExcelJS.Buffer): string {
  const globalBuf = (globalThis as unknown as { Buffer?: { from(data: ArrayBuffer): { toString(enc: string): string } } }).Buffer;
  if (globalBuf) {
    return globalBuf.from(buffer as ArrayBuffer).toString('base64');
  }
  const uint8 = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer as ArrayBuffer);
  let binary = '';
  const len = uint8.length;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8[i]);
  }
  return btoa(binary);
}

async function uploadCarbonCopyAsync(
  buffer: ExcelJS.Buffer,
  monthString: string,
  outputFileName: string,
  options?: ExportToExcelOptions
): Promise<string> {
  let finalFileName = outputFileName;
  try {
    const fileBase64 = bufferToBase64(buffer);
    const fileSize = (buffer as { byteLength?: number; length?: number }).byteLength ?? (buffer as { byteLength?: number; length?: number }).length ?? 0;

    const { data, error } = await supabase.functions.invoke('upload-month-attachment', {
      body: {
        month: monthString,
        fileName: outputFileName,
        fileSize,
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        fileBase64,
        isCarbonCopy: true,
        employeeNumber: options?.employeeNumber || undefined,
        employeeInitials: options?.initials || undefined,
        isApproval: Boolean(options?.isApproval),
      }
    });

    if (!error && data?.fileName) {
      finalFileName = data.fileName;
      const storageKey = `jatt_export_ver_${options?.employeeNumber || ''}_${options?.initials || ''}_${monthString}`;
      const verMatch = data.fileName.match(/_v(\d+\.\d+)\.xlsx$/i);
      if (verMatch) {
        try {
          localStorage.setItem(storageKey, `v${verMatch[1]}`);
        } catch {
          // Ignore localStorage errors
        }
      }
    }
  } catch (uploadErr) {
    console.warn('Carbon copy upload skipped or failed:', uploadErr);
    const storageKey = `jatt_export_ver_${options?.employeeNumber || ''}_${options?.initials || ''}_${monthString}`;
    try {
      const prev = localStorage.getItem(storageKey);
      let next: string;
      if (options?.isApproval) {
        next = prev && prev.startsWith('v1.') ? incrementMinorVersion(prev) : 'v1.0';
      } else {
        next = prev ? incrementMinorVersion(prev) : 'v0.1';
      }
      localStorage.setItem(storageKey, next);
      const base = outputFileName.replace(/(_v\d+\.\d+)?\.xlsx$/i, '');
      finalFileName = `${base}_${next}.xlsx`;
    } catch {
      // Ignore localStorage errors
    }
  }
  return finalFileName;
}

interface TemplateCellInfo {
  col: number;
  value?: ExcelJS.CellValue;
  numFmt?: string;
  alignment?: Partial<ExcelJS.Alignment>;
  border?: Partial<ExcelJS.Borders>;
  fill?: ExcelJS.Fill;
  font?: Partial<ExcelJS.Font>;
}

const WEEKEND_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFF8CBAD' }
};

function captureRowTemplateCells(sheet: ExcelJS.Worksheet, rowNum: number): TemplateCellInfo[] {
  const result: TemplateCellInfo[] = [];
  sheet.getRow(rowNum).eachCell({ includeEmpty: true }, (c, col) => {
    if (col <= 11) {
      result.push({
        col,
        value: c.value,
        numFmt: c.numFmt,
        alignment: c.alignment ? { ...c.alignment } : undefined,
        border: c.border ? JSON.parse(JSON.stringify(c.border)) : undefined,
        fill: c.fill ? JSON.parse(JSON.stringify(c.fill)) : undefined,
        font: c.font ? { ...c.font } : undefined
      });
    }
  });
  return result;
}

function applyTemplateToRow(targetRow: ExcelJS.Row, templateCells: TemplateCellInfo[]): void {
  for (const tmpl of templateCells) {
    const cell = targetRow.getCell(tmpl.col);
    if (tmpl.numFmt) cell.numFmt = tmpl.numFmt;
    if (tmpl.alignment) cell.alignment = { ...tmpl.alignment };
    if (tmpl.border) cell.border = JSON.parse(JSON.stringify(tmpl.border));
    if (tmpl.font) cell.font = { ...tmpl.font };
    if (tmpl.fill) cell.fill = JSON.parse(JSON.stringify(tmpl.fill));
  }
}

export function getPositionDisplayName(project?: Project): string {
  const parts = [project?.customerId?.trim(), project?.customerDepartmentId?.trim()].filter(Boolean) as string[];
  return parts.join(', ');
}

function populateHeader(
  sheet: ExcelJS.Worksheet,
  project: Project | undefined,
  monthDate: Date,
  options?: ExportToExcelOptions
): void {
  const firstName = options?.userProfile?.firstName ?? options?.firstName;
  const lastName = options?.userProfile?.lastName ?? options?.lastName;
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || options?.employeeName || '';
  const employeeNumber = options?.userProfile?.employeeNumber ?? options?.employeeNumber ?? '';

  const positionName = getPositionDisplayName(project);
  const contractNumber = project?.contractId || project?.contract || '';
  const spfContractId = project?.contract || '';

  // Direct cell assignments
  sheet.getCell('C3').value = fullName;
  sheet.getCell('C4').value = employeeNumber;
  sheet.getCell('C5').value = monthDate;
  sheet.getCell('I3').value = positionName;
  sheet.getCell('I4').value = contractNumber;
  sheet.getCell('I5').value = spfContractId;

  // Placeholder replacements across all rows (including signature rows)
  for (let r = 1; r <= 47; r++) {
    const row = sheet.getRow(r);
    row.eachCell({ includeEmpty: false }, cell => {
      if (typeof cell.value === 'string') {
        if (cell.value.includes('{Employee_Name}')) {
          cell.value = cell.value.replace('{Employee_Name}', fullName);
        }
        if (cell.value.includes('{Employee_Number}')) {
          cell.value = cell.value.replace('{Employee_Number}', employeeNumber);
        }
        if (cell.value.includes('{Position_Name}')) {
          cell.value = cell.value.replace('{Position_Name}', positionName);
        }
        if (cell.value.includes('{Position_Contract_Number}')) {
          cell.value = cell.value.replace('{Position_Contract_Number}', contractNumber);
        }
        if (cell.value.includes('{Postion_Service_Type}')) {
          cell.value = cell.value.replace('{Postion_Service_Type}', spfContractId);
        }
        if (cell.value.includes('{Position_Service_Type}')) {
          cell.value = cell.value.replace('{Position_Service_Type}', spfContractId);
        }
        if (cell.value.includes('{Month} {Year}')) {
          cell.value = monthDate;
        }
      } else if (cell.value && typeof cell.value === 'object' && 'formula' in cell.value) {
        if (typeof (cell.value as { result?: unknown }).result === 'string' && ((cell.value as { result: string }).result).includes('{Employee_Name}')) {
          cell.value = {
            formula: (cell.value as { formula: string }).formula,
            result: fullName
          };
        }
      }
    });
  }

  // Explicitly ensure cell A46 is assigned the formula pointing to C3 with fullName as cached result
  const cellA46 = sheet.getCell('A46');
  cellA46.value = { formula: 'C3', result: fullName };
}

function populateDayRow(
  targetRow: ExcelJS.Row,
  rowNum: number,
  day: number,
  dateStr: string,
  dayBlocks: ExportBlock[] | undefined,
  templateCells: TemplateCellInfo[]
): number {
  targetRow.height = 20;
  applyTemplateToRow(targetRow, templateCells);

  const currentDate = parseISO(dateStr);
  const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const dayOfWeekText = format(currentDate, 'EEEE', { locale: de });

  const cellA = targetRow.getCell(1);
  cellA.value = day;

  const cellB = targetRow.getCell(2);
  cellB.value = dayOfWeekText;

  if (isWeekend) {
    cellA.fill = WEEKEND_FILL;
    cellB.fill = WEEKEND_FILL;
  }

  let dayHours = 0;
  if (dayBlocks && dayBlocks.length > 0) {
    const arrivedStr = dayBlocks[0].timeArrived;
    const leftStr = dayBlocks[dayBlocks.length - 1].timeLeft;
    const totalBreakHrs = dayBlocks.reduce((acc: number, b: ExportBlock) => acc + (b.breakHours || 0), 0);
    const desc = dayBlocks.map((b: ExportBlock) => b.description).filter(Boolean).join(', ');
    const loc = Array.from(new Set(dayBlocks.map((b: ExportBlock) => b.location).filter(Boolean))).join(', ');

    const arrivedFraction = timeStringToExcelFraction(arrivedStr);
    const leftFraction = timeStringToExcelFraction(leftStr);

    const cellE = targetRow.getCell(5);
    cellE.value = arrivedFraction;
    cellE.numFmt = 'h:mm';

    const cellF = targetRow.getCell(6);
    cellF.value = leftFraction;
    cellF.numFmt = 'h:mm';

    const cellG = targetRow.getCell(7);
    cellG.value = totalBreakHrs > 0 ? Math.round(totalBreakHrs * 100) / 100 : null;
    cellG.numFmt = '0.00';

    targetRow.getCell(8).value = desc || null;
    targetRow.getCell(11).value = loc || null;

    if (dayBlocks.some(b => typeof b.workMinutes === 'number' && b.workMinutes > 0)) {
      const totalWorkMins = dayBlocks.reduce((acc, b) => acc + (b.workMinutes || 0), 0);
      dayHours = Math.round((totalWorkMins / 60) * 100) / 100;
    } else if (arrivedFraction !== null && leftFraction !== null) {
      const rawDiffHours = (leftFraction - arrivedFraction) * 24;
      dayHours = Math.max(0, Math.round((rawDiffHours - totalBreakHrs) * 100) / 100);
    }
  } else {
    targetRow.getCell(5).value = null;
    targetRow.getCell(6).value = null;
    targetRow.getCell(7).value = null;
    targetRow.getCell(8).value = null;
    targetRow.getCell(11).value = null;
  }

  const formulaStr = `ROUND(IF(SUM(E${rowNum}:F${rowNum})<>0,HOUR(F${rowNum}-E${rowNum})+(MINUTE(F${rowNum}-E${rowNum})/60)-G${rowNum}),2)`;
  const cellC = targetRow.getCell(3);
  cellC.value = {
    formula: formulaStr,
    result: dayHours
  };
  cellC.numFmt = '0.00;-0.00;;@';

  return dayHours;
}

function populateSummaryRows(
  sheet: ExcelJS.Worksheet,
  daysInMonth: number,
  totalProjectHours: number,
  row9TemplateCells: TemplateCellInfo[],
  row10TemplateCells: TemplateCellInfo[],
  fullName?: string
): void {
  const lastDayRow = 7 + daysInMonth;
  const summaryRow = 39;
  const totalDaysRow = 40;

  // Clear extra rows if month has fewer than 31 days (up to row 38)
  for (let r = lastDayRow + 1; r <= 38; r++) {
    const extraRow = sheet.getRow(r);
    extraRow.height = 20;
    for (let c = 1; c <= 11; c++) {
      const cell = extraRow.getCell(c);
      cell.value = null;
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { theme: 0 }
      };
      cell.border = {};
    }
  }

  // Summary hours row (always on row 39)
  const sRow = sheet.getRow(summaryRow);
  sRow.height = 20;
  applyTemplateToRow(sRow, row9TemplateCells);
  for (const tmpl of row9TemplateCells) {
    if (tmpl.value !== null && tmpl.value !== undefined) {
      sRow.getCell(tmpl.col).value = tmpl.value;
    }
  }
  const roundedTotalHours = Math.round(totalProjectHours * 100) / 100;
  sRow.getCell(1).value = 'Total';
  sRow.getCell(2).value = 'Stunden';
  sRow.getCell(3).value = {
    formula: 'SUM(C8:C38)',
    result: roundedTotalHours
  };
  sRow.getCell(3).numFmt = '0.00';

  // Total days row (always on row 40)
  const tdRow = sheet.getRow(totalDaysRow);
  tdRow.height = 20;
  applyTemplateToRow(tdRow, row10TemplateCells);
  for (const tmpl of row10TemplateCells) {
    if (tmpl.value !== null && tmpl.value !== undefined) {
      tdRow.getCell(tmpl.col).value = tmpl.value;
    }
  }
  tdRow.getCell(1).value = 'Total';
  tdRow.getCell(2).value = 'Tage';
  tdRow.getCell(3).value = {
    formula: `C${summaryRow}/8.4`,
    result: Math.round((roundedTotalHours / 8.4) * 100) / 100
  };
  tdRow.getCell(3).numFmt = '0.00';

  // Clear intermediate rows between totalDaysRow and signature rows (up to row 45)
  for (let r = totalDaysRow + 1; r <= 45; r++) {
    const emptyRow = sheet.getRow(r);
    emptyRow.height = 20;
    for (let c = 1; c <= 11; c++) {
      const cell = emptyRow.getCell(c);
      cell.value = null;
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { theme: 0 }
      };
      cell.border = {};
    }
  }

  // Row 46: Signature name line
  const row46 = sheet.getRow(46);
  row46.height = 20;
  const c3Val = sheet.getCell('C3').value;
  const nameResult = typeof c3Val === 'string' && c3Val ? c3Val : (fullName || '');
  row46.getCell(1).value = { formula: 'C3', result: nameResult };
  row46.getCell(1).font = { name: 'Calibri', size: 12, family: 2 };
  row46.getCell(1).alignment = { vertical: 'middle' };
  row46.getCell(1).border = { bottom: { style: 'thin' } };
  row46.getCell(2).border = { bottom: { style: 'thin' } };
  row46.getCell(3).border = { bottom: { style: 'thin' } };

  // Row 47: Signature title
  const row47 = sheet.getRow(47);
  row47.height = 20;
  row47.getCell(1).value = 'Visum Mitarbeiter';
  row47.getCell(1).font = { name: 'Calibri', size: 12, family: 2 };
  row47.getCell(1).alignment = { vertical: 'middle' };
}

function groupEntriesByProject(
  entries: TimeEntry[],
  monthString: string,
  locationMap: Map<string, string>,
  positions?: Project[]
): Map<string, Map<string, ExportBlock[]>> {
  const projectData = new Map<string, Map<string, ExportBlock[]>>();
  const sortedEntries = [...entries].sort((a, b) => a.date.localeCompare(b.date));

  for (const entry of sortedEntries) {
    if (!entry.date.startsWith(monthString)) continue;

    const breakBlockMinutes = (entry.projects || [])
      .filter(p => p.projectId === 'break')
      .reduce((sum, p) => sum + (p.minutes || 0), 0);
    const effectiveBreakMins = breakBlockMinutes > 0 ? breakBlockMinutes : (entry.breakDurationMinutes || 0);

    const blocks = calculateTimeBlocks(
      entry.timeArrived,
      entry.timeLeft,
      effectiveBreakMins,
      consolidateProjects(entry.projects),
      positions
    );

    const locName = locationMap.get(entry.locationId) || 'Unknown Location';

    for (const block of blocks) {
      if (!projectData.has(block.projectId)) {
        projectData.set(block.projectId, new Map());
      }

      const pDates = projectData.get(block.projectId)!;
      if (!pDates.has(entry.date)) {
        pDates.set(entry.date, []);
      }

      const blockBreakHours = block.breakDurationMinutes > 0 ? Math.round((block.breakDurationMinutes / 60) * 100) / 100 : null;

      pDates.get(entry.date)!.push({
        date: entry.date,
        timeArrived: block.timeArrived,
        timeLeft: block.timeLeft,
        breakHours: blockBreakHours,
        description: block.description || '',
        location: locName,
        workMinutes: block.workMinutes
      });
    }
  }

  return projectData;
}

function renderProjectSheet(
  wb: ExcelJS.Workbook,
  templateSheet: ExcelJS.Worksheet,
  projectObj: Project | undefined,
  pName: string,
  datesMap: Map<string, ExportBlock[]>,
  daysInMonth: number,
  monthString: string,
  monthDate: Date,
  row8TemplateCells: TemplateCellInfo[],
  row9TemplateCells: TemplateCellInfo[],
  row10TemplateCells: TemplateCellInfo[],
  options?: ExportToExcelOptions
): void {
  const displayName = getPositionDisplayName(projectObj);
  const baseName = (displayName || pName || 'Sheet').substring(0, 31).replace(/[\\/?*[\]:]/g, '').trim() || 'Sheet';
  let safeSheetName = baseName;
  if (wb.getWorksheet(safeSheetName)) {
    let counter = 2;
    while (wb.getWorksheet(`${baseName.substring(0, 26)} (${counter})`)) {
      counter++;
    }
    safeSheetName = `${baseName.substring(0, 26)} (${counter})`;
  }

  const newSheet = wb.addWorksheet(safeSheetName);
  const sheetModel = JSON.parse(JSON.stringify(templateSheet.model));
  sheetModel.name = safeSheetName;
  sheetModel.id = newSheet.id;
  newSheet.model = sheetModel;

  // Preserve merged cells from templateSheet (e.g. C5:E5 for the Month/Year field)
  const templateMerges = templateSheet.model.merges || [];
  for (const mergeRange of templateMerges) {
    try {
      newSheet.mergeCells(mergeRange);
    } catch {
      // Ignore duplicate merge errors
    }
  }
  if (!newSheet.getCell('C5').isMerged) {
    newSheet.mergeCells('C5:E5');
  }

  // Populate header information
  populateHeader(newSheet, projectObj, monthDate, options);

  let totalProjectHours = 0;

  // Fill days of the month (Rows 8 to 7 + daysInMonth)
  for (let day = 1; day <= daysInMonth; day++) {
    const rowNum = 7 + day;
    const dateStr = `${monthString}-${String(day).padStart(2, '0')}`;
    const dayBlocks = datesMap.get(dateStr);
    const targetRow = newSheet.getRow(rowNum);

    const dayHours = populateDayRow(
      targetRow,
      rowNum,
      day,
      dateStr,
      dayBlocks,
      row8TemplateCells
    );
    totalProjectHours += dayHours;
  }

  const firstName = options?.userProfile?.firstName ?? options?.firstName;
  const lastName = options?.userProfile?.lastName ?? options?.lastName;
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || options?.employeeName || '';

  // Set summary rows immediately after the last day
  populateSummaryRows(
    newSheet,
    daysInMonth,
    totalProjectHours,
    row9TemplateCells,
    row10TemplateCells,
    fullName
  );

  if (newSheet.pageSetup) {
    newSheet.pageSetup.printArea = 'A1:K47';
    newSheet.pageSetup.fitToPage = true;
    newSheet.pageSetup.fitToWidth = 1;
    newSheet.pageSetup.fitToHeight = 9;
  }
}

export const exportToExcel = async (
  entries: TimeEntry[],
  projects: Project[],
  locations: Location[],
  monthString: string,
  fileName?: string,
  writeToDisk: boolean = true,
  options?: ExportToExcelOptions
): Promise<ExcelJS.Workbook | null> => {
  const projectMap = new Map(projects.map(p => [p.id, p.name]));
  const locationMap = new Map(locations.map(l => [l.id, l.name]));

  const projectData = groupEntriesByProject(entries, monthString, locationMap, projects);

  if (projectData.size === 0) {
    alert("No entries to export for this month!");
    return null;
  }

  const templateBuf = await loadTemplateBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(templateBuf);

  const templateSheet = wb.getWorksheet('Tabelle1') || wb.worksheets[0];
  if (!templateSheet) {
    throw new Error('Template worksheet not found in ZE_Template.xlsx');
  }

  const row8TemplateCells = captureRowTemplateCells(templateSheet, 8);
  const row9TemplateCells = captureRowTemplateCells(templateSheet, 9);
  const row10TemplateCells = captureRowTemplateCells(templateSheet, 10);

  const daysInMonth = getDaysInMonth(parseISO(`${monthString}-01`));
  const [yStr, mStr] = monthString.split('-');
  const yearNum = parseInt(yStr, 10);
  const monthNum = parseInt(mStr, 10);
  const monthDate = new Date(Date.UTC(yearNum, monthNum - 1, 1));

  // Create a sheet for each project from template
  for (const [projectId, datesMap] of projectData.entries()) {
    const projectObj = projects.find(p => p.id === projectId);
    const pName = projectObj?.name || projectMap.get(projectId) || `Project ${projectId}`;

    renderProjectSheet(
      wb,
      templateSheet,
      projectObj,
      pName,
      datesMap,
      daysInMonth,
      monthString,
      monthDate,
      row8TemplateCells,
      row9TemplateCells,
      row10TemplateCells,
      options
    );
  }

  // Force full recalculation on load in Excel
  wb.calcProperties.fullCalcOnLoad = true;

  // Remove the initial template sheet so only project sheets remain
  wb.removeWorksheet(templateSheet.id);

  if (writeToDisk || options?.uploadCopy) {
    const defaultVer = options?.isApproval ? 'v1.0' : 'v0.1';
    const defaultFileName = getMonthlyReportFileName(monthString, options?.employeeNumber, options?.initials, defaultVer);
    let outputFileName = fileName || defaultFileName;
    if (!outputFileName.endsWith('.xlsx')) {
      outputFileName = `${outputFileName}.xlsx`;
    }

    const buffer = await wb.xlsx.writeBuffer();
    let finalFileName = outputFileName;

    if (options?.uploadCopy) {
      finalFileName = await uploadCarbonCopyAsync(buffer, monthString, outputFileName, options);
    }

    if (writeToDisk) {
      saveWorkbookFile(buffer, finalFileName);
    }
  }

  return wb;
};

export const downloadTemplate = (
  projects: Project[],
  monthString: string,
  writeToDisk: boolean = true
): XLSX.WorkBook | null => {
  if (projects.length === 0) {
    if (writeToDisk) {
      alert("Please configure at least one project in Settings first!");
    }
    return null;
  }

  const wb = XLSX.utils.book_new();
  const daysInMonth = getDaysInMonth(parseISO(`${monthString}-01`));

  for (const project of projects) {
    const rows: Record<string, string | number>[] = [];
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${monthString}-${String(i).padStart(2, '0')}`;
      rows.push({
        Date: dateStr,
        'Time Arrived': '',
        'Time Left': '',
        'Break Duration (hrs)': '',
        'What was worked on': '',
        ' ': '',
        '  ': '',
        Location: ''
      });
    }

    const ws = XLSX.utils.json_to_sheet(rows);

    const wscols = [
      { wch: 12 }, // Date
      { wch: 12 }, // Time Arrived
      { wch: 12 }, // Time Left
      { wch: 22 }, // Break
      { wch: 40 }, // Description
      { wch: 15 }, // Empty 1
      { wch: 15 }, // Empty 2
      { wch: 20 }, // Location
    ];
    ws['!cols'] = wscols;

    const displayName = getPositionDisplayName(project);
    const safeSheetName = (displayName || project.name).substring(0, 31).replace(/[\\/?*[\]:]/g, '');
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName || 'Sheet');
  }

  if (writeToDisk) {
    XLSX.writeFile(wb, `TimeReportTemplate_${monthString}.xlsx`);
  }
  return wb;
};
